import { modelState } from "@/utils/modelState";
import { toDateTimeString } from "@/utils/utils";
import { minBy } from "lodash";
import type { ReservationState } from "./types";

export const ReservationArrivalTransferMode = Symbol("ReservationArrivalTransferMode");
export interface ReservationArrivalTransferMode {
    [ReservationArrivalTransferMode]: boolean;
}

export const ReservationFlightArrivalTransferMode = Symbol("ReservationFlightArrivalTransferMode");
export interface ReservationFlightArrivalTransferMode extends ReservationArrivalTransferMode {
    [ReservationFlightArrivalTransferMode]: boolean;
    FlightNumber: modelState<string>;
    ArrivalTime: modelState<Date>;
    Details: modelState<string>;
}

export function isReservationFlightArrivalTransferMode(val: ReservationArrivalTransferMode): val is ReservationFlightArrivalTransferMode {
    return val && (val as ReservationFlightArrivalTransferMode)[ReservationFlightArrivalTransferMode] === true;
}

export const ReservationOtherArrivalTransferMode = Symbol("ReservationOtherArrivalTransferMode");
export interface ReservationOtherArrivalTransferMode extends ReservationArrivalTransferMode {
    [ReservationOtherArrivalTransferMode]: boolean;
    Details: modelState<string>;
}

export function isReservationOtherArrivalTransferMode(val: ReservationArrivalTransferMode): val is ReservationOtherArrivalTransferMode {
    return val && (val as ReservationOtherArrivalTransferMode)[ReservationOtherArrivalTransferMode] === true;
}

export interface ReservationArrivalTransferModeDto {
    $type: string;
    value: ReservationArrivalTransferModeValueDto;
}

export interface ReservationArrivalTransferModeValueDto {
    //
}

export interface ReservationFlightArrivalTransferModeValueDto extends ReservationArrivalTransferModeValueDto {
    flightNumber: string;
    arrivalTime: string;
    details: string;
}

export interface ReservationOtherArrivalTransferModeValueDto extends ReservationArrivalTransferModeValueDto {
    details: string;
}

export function serializeReservationArrivalTransferMode(action: ReservationArrivalTransferMode): ReservationArrivalTransferModeDto {
    if (!action) {
        return null;
    }

    if (isReservationFlightArrivalTransferMode(action)) {
        return {
            $type: "Flight",
            value: {
                FlightNumber: action.FlightNumber.value,
                ArrivalTime: toDateTimeString(action.ArrivalTime.value),
                Details: action.Details.value
            }
        };
    }

    if (isReservationOtherArrivalTransferMode(action)) {
        return {
            $type: "Other",
            value: {
                Details: action.Details.value
            }
        };
    }

    throw new Error("Invalid mode type");
}

export function deserializeReservationArrivalTransferMode(dto: ReservationArrivalTransferModeDto): ReservationArrivalTransferMode {
    if (!dto) {
        return null;
    }

    switch (dto.$type) {
        case "Flight":
            const flightValue = dto.value as ReservationFlightArrivalTransferModeValueDto;

            const flightMode = createReservationFlightArrivalTransferMode(null);

            flightMode.FlightNumber.setValue(flightValue.flightNumber);
            flightMode.FlightNumber.commit();

            flightMode.ArrivalTime.setValue(new Date(flightValue.arrivalTime));
            flightMode.ArrivalTime.commit();

            flightMode.Details.setValue(flightValue.details);
            flightMode.Details.commit();

            return flightMode;
        case "Other":
            const otherValue = dto.value as ReservationOtherArrivalTransferModeValueDto;

            const otherMode = createReservationOtherArrivalTransferMode();
            otherMode.Details.setValue(otherValue.details);

            return otherMode;
        default:
            throw new Error("Invalid mode type");
    }
}

export function getReservationArrivalTransferModeName(mode: ReservationArrivalTransferMode | null): string {
    if (!mode) {
        return "None";
    }

    if (isReservationFlightArrivalTransferMode(mode)) {
        return "Flight";
    }

    if (isReservationOtherArrivalTransferMode(mode)) {
        return "Other";
    }

    throw new Error("Invalid mode type");
}

export function createReservationFlightArrivalTransferMode(arrivalTime: Date | null): ReservationFlightArrivalTransferMode {
    const mode: ReservationFlightArrivalTransferMode = {
        [ReservationArrivalTransferMode]: true,
        [ReservationFlightArrivalTransferMode]: true,
        FlightNumber: modelState(""),
        ArrivalTime: modelState(arrivalTime ?? new Date()),
        Details: modelState("")
    };

    return mode;
};

export function createReservationOtherArrivalTransferMode(): ReservationOtherArrivalTransferMode {
    const mode: ReservationOtherArrivalTransferMode = {
        [ReservationArrivalTransferMode]: true,
        [ReservationOtherArrivalTransferMode]: true,
        Details: modelState("")
    };

    return mode;
}

export function createReservationArrivalTransferMode(name: string, reservationState: ReservationState): ReservationArrivalTransferMode {

    const roomBookings = reservationState.RoomBookings.toArray();

    let earliestRoom: Date | null = null;;

    if (roomBookings.length > 0) {
        earliestRoom = minBy(roomBookings, r => r.CheckIn.value.getTime()).CheckIn.value;
    }

    switch (name) {
        case "None":
            return null;
        case "Flight":
            return createReservationFlightArrivalTransferMode(earliestRoom);
        case "Other":
            return createReservationOtherArrivalTransferMode();
        default:
            throw new Error("Invalid mode name");
    }
}
